import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { ImageIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
const CREATIVES = [{
  id: 1,
  name: 'XUV400 Launch',
  type: 'image',
  formats: ['1:1', '9:16', '16:9'],
  date: '2024-01-15'
}, {
  id: 2,
  name: 'Thar Roxx Campaign',
  type: 'video',
  formats: ['1:1', '9:16'],
  date: '2024-01-14'
}, {
  id: 3,
  name: 'Scorpio-N Promo',
  type: 'image',
  formats: ['16:9', '4:5'],
  date: '2024-01-13'
}, {
  id: 4,
  name: 'BE6 EV Launch',
  type: 'image',
  formats: ['1:1', '9:16', '16:9', '4:5'],
  date: '2024-01-12'
}];
const PLATFORM_FORMATS = {
  'Facebook Feed': '1:1',
  'Instagram Story': '9:16',
  'Google Display': '16:9',
  'YouTube Shorts': '9:16',
  Carousel: '4:5'
};
export function CreativeSelection() {
  const navigate = useNavigate();
  const [selectedCreative, setSelectedCreative] = useState<number | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('Facebook Feed');
  const handleCreativeSelect = (creativeId: number) => {
    setSelectedCreative(creativeId);
    const creative = CREATIVES.find(c => c.id === creativeId);
    if (creative) {
      const suggestedFormat = PLATFORM_FORMATS[selectedPlatform];
      if (creative.formats.includes(suggestedFormat)) {
        setSelectedFormat(suggestedFormat);
      } else {
        setSelectedFormat(creative.formats[0]);
      }
    }
  };
  const selectedCreativeData = CREATIVES.find(c => c.id === selectedCreative);
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-1278">
      <Sidebar data-id="element-1279" />
      <main className="flex-1" data-id="element-1280">
        <Header data-id="element-1281" />
        <StepIndicator currentStep={3} steps={STEPS} data-id="element-1282" />
        <div className="px-6 py-8" data-id="element-1283">
          <div className="max-w-5xl mx-auto" data-id="element-1284">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-1285">
              Step 2: Select Master Creative
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-1286">
              Choose from your creative library with format mapping
            </p>

            <div className="grid grid-cols-3 gap-6" data-id="element-1287">
              <div className="col-span-2 space-y-6" data-id="element-1288">
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1289">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1290">
                    Creative Library
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1291">
                    {CREATIVES.map(creative => <button key={creative.id} onClick={() => handleCreativeSelect(creative.id)} className={`p-4 rounded-lg border-2 text-left transition-all ${selectedCreative === creative.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1292">
                        <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 flex items-center justify-center" data-id="element-1293">
                          {creative.type === 'image' ? <ImageIcon className="text-gray-400" size={40} data-id="element-1294" /> : <VideoIcon className="text-gray-400" size={40} data-id="element-1295" />}
                        </div>
                        <div className="flex items-center justify-between mb-2" data-id="element-1296">
                          <span className="font-medium text-gray-800" data-id="element-1297">
                            {creative.name}
                          </span>
                          {selectedCreative === creative.id && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-1298" />}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-1299">
                          <span data-id="element-1300">
                            {creative.formats.length} formats available
                          </span>
                          <span data-id="element-1301">{creative.date}</span>
                        </div>
                      </button>)}
                  </div>
                </div>

                {selectedCreativeData && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1302">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1303">
                      Available Formats
                    </h3>
                    <div className="grid grid-cols-3 gap-3" data-id="element-1304">
                      {selectedCreativeData.formats.map(format => <button key={format} onClick={() => setSelectedFormat(format)} className={`p-4 rounded-lg border-2 transition-all ${selectedFormat === format ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1305">
                          <div className="w-full h-20 bg-gray-100 rounded mb-2 flex items-center justify-center" data-id="element-1306">
                            {selectedCreativeData.type === 'image' ? <ImageIcon className="text-gray-400" size={24} data-id="element-1307" /> : <VideoIcon className="text-gray-400" size={24} data-id="element-1308" />}
                          </div>
                          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-1309">
                            {format}
                          </p>
                        </button>)}
                    </div>
                  </div>}
              </div>

              <div className="space-y-6" data-id="element-1310">
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1311">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1312">
                    Platform Mapping
                  </h3>
                  <div className="space-y-3" data-id="element-1313">
                    {Object.entries(PLATFORM_FORMATS).map(([platform, format]) => <button key={platform} onClick={() => setSelectedPlatform(platform)} className={`w-full p-3 rounded-lg border text-left transition-all ${selectedPlatform === platform ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1314">
                          <p className="font-medium text-gray-800 text-sm" data-id="element-1315">
                            {platform}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-1316">
                            Suggested: {format}
                          </p>
                        </button>)}
                  </div>
                </div>

                {selectedCreativeData && selectedFormat && <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-1317">
                    <h4 className="font-medium text-green-900 mb-2" data-id="element-1318">
                      Selection Summary
                    </h4>
                    <div className="text-sm text-green-800 space-y-1" data-id="element-1319">
                      <p data-id="element-1320">
                        <strong data-id="element-1321">Creative:</strong> {selectedCreativeData.name}
                      </p>
                      <p data-id="element-1322">
                        <strong data-id="element-1323">Format:</strong> {selectedFormat}
                      </p>
                      <p data-id="element-1324">
                        <strong data-id="element-1325">Platform:</strong> {selectedPlatform}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="flex justify-between mt-6" data-id="element-1326">
              <button onClick={() => navigate('/dealerization/step-1')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1327">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-3')} disabled={!selectedCreative || !selectedFormat} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-1328">
                Continue to Styling Template
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}